import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import clsx from "clsx";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { signUpUser } from "../features/auth/authSlice";

type FormErrors = {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
};

export default function SignUp() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { signupStatus, signupError, signupMessage, signupFieldErrors } =
    useAppSelector((state) => state.auth);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const isSubmitting = signupStatus === "loading";
  const baseInputClasses =
    "w-full bg-[#F7FAFC] px-4 py-3 rounded-md focus:outline-none placeholder-gray-400 transition-colors";
  const inputClasses = (hasError: boolean) =>
    clsx(
      baseInputClasses,
      hasError
        ? "border border-red-400 focus:ring-2 focus:ring-red-400 focus:border-red-400"
        : "border border-gray-200 focus:ring-2 focus:ring-lime-400 focus:border-transparent",
    );

  const validateForm = (): FormErrors => {
    const errors: FormErrors = {};
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

    if (!trimmedName) {
      errors.name = "Name is required.";
    }

    if (!trimmedEmail) {
      errors.email = "Email is required.";
    } else if (!emailRegex.test(trimmedEmail)) {
      errors.email = "Enter a valid email address.";
    } else if (trimmedEmail.length > 254) {
      errors.email = "Email must be 254 characters or fewer.";
    }

    if (!password) {
      errors.password = "Password is required.";
    } else if (password.length > 128) {
      errors.password = "Password must be 128 characters or fewer.";
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Confirm password is required.";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords must match.";
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    setSubmissionError(null);

    try {
      await dispatch(
        signUpUser({
          name: name.trim(),
          email: email.trim(),
          password,
          confirm_password: confirmPassword,
        }),
      ).unwrap();
    } catch (error) {
      setSubmissionError(
        typeof error === "string"
          ? error
          : "Unable to complete sign up. Please try again.",
      );
    }
  };

  const feedbackError = submissionError ?? signupError;
  const feedbackSuccess =
    signupStatus === "succeeded" && signupMessage ? signupMessage : null;

  useEffect(() => {
    if (!signupFieldErrors) {
      return;
    }

    const mappedErrors: FormErrors = {};
    if (signupFieldErrors.name?.length) {
      mappedErrors.name = signupFieldErrors.name[0];
    }
    if (signupFieldErrors.email?.length) {
      mappedErrors.email = signupFieldErrors.email[0];
    }
    if (
      signupFieldErrors.password?.length ||
      signupFieldErrors.password1?.length
    ) {
      mappedErrors.password =
        signupFieldErrors.password?.[0] ?? signupFieldErrors.password1?.[0];
    }
    if (
      signupFieldErrors.confirm_password?.length ||
      signupFieldErrors.confirmPassword?.length
    ) {
      mappedErrors.confirmPassword =
        signupFieldErrors.confirm_password?.[0] ??
        signupFieldErrors.confirmPassword?.[0];
    }

    setFormErrors((prev) => ({ ...prev, ...mappedErrors }));

    const firstFieldMessage =
      signupFieldErrors.email?.[0] ??
      signupFieldErrors.name?.[0] ??
      signupFieldErrors.password?.[0] ??
      signupFieldErrors.confirm_password?.[0] ??
      signupFieldErrors.confirmPassword?.[0] ??
      signupFieldErrors.password1?.[0] ??
      Object.values(signupFieldErrors)[0]?.[0];

    if (firstFieldMessage) {
      setSubmissionError(firstFieldMessage);
    } else if (signupError) {
      setSubmissionError(signupError);
    }
  }, [signupFieldErrors, signupError]);

  useEffect(() => {
    if (signupStatus === "succeeded") {
      setName("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setRememberMe(false);
      setShowPassword(false);
      setShowConfirmPassword(false);

      const successMessage = signupMessage ?? "Account created successfully.";
      toast.success(successMessage, {
        description: "You can now sign in with your new credentials.",
      });
      navigate("/login", { replace: true });
    }
  }, [signupStatus, signupMessage, navigate]);

  useEffect(() => {
    if (feedbackError) {
      toast.error(feedbackError);
    }
  }, [feedbackError]);

  const handleGoogleLogin = () => {
    console.log("Google login clicked");
  };

  const handleFacebookLogin = () => {
    console.log("Facebook login clicked");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Section - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-12 lg:px-16 py-1 bg-white">
        <div className="max-w-md mx-auto w-full">
          {/* Header */}
          <h1 className="text-5xl font-bold text-[#171923] mb-2">
            Get Started Now
          </h1>
          <p className="text-[#171923] text-lg mb-8">
            Enter your credentials to access your account
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {feedbackError && (
              <div
                className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                role="alert"
                aria-live="assertive"
              >
                {feedbackError}
              </div>
            )}

            {feedbackSuccess && (
              <div
                className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700"
                role="status"
                aria-live="polite"
              >
                {feedbackSuccess}
              </div>
            )}

            {/* Name Input */}
            <div>
              <label className="block text-base font-medium text-[#718096] mb-2">
                Name
              </label>
              <input
                type="text"
                placeholder="john Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClasses(Boolean(formErrors.name))}
                aria-invalid={Boolean(formErrors.name)}
                aria-describedby={formErrors.name ? "name-error" : undefined}
                required
              />
              {formErrors.name && (
                <p id="name-error" className="mt-1 text-sm text-red-600">
                  {formErrors.name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-base font-medium text-[#718096] mb-2">
                E-mail
              </label>
              <input
                type="email"
                placeholder="example@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClasses(Boolean(formErrors.email))}
                aria-invalid={Boolean(formErrors.email)}
                aria-describedby={formErrors.email ? "email-error" : undefined}
                required
              />
              {formErrors.email && (
                <p id="email-error" className="mt-1 text-sm text-red-600">
                  {formErrors.email}
                </p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-base font-medium text-[#718096] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClasses(Boolean(formErrors.password))}
                  aria-invalid={Boolean(formErrors.password)}
                  aria-describedby={
                    formErrors.password ? "password-error" : undefined
                  }
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 px-1 h-2/3 top-1/2 border-l-2 border-[#CFD9E0] -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p id="password-error" className="mt-1 text-sm text-red-600">
                  {formErrors.password}
                </p>
              )}
            </div>

            {/* Confirm Password Input */}
            <div>
              <label className="block text-base font-medium text-[#718096] mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClasses(Boolean(formErrors.confirmPassword))}
                  aria-invalid={Boolean(formErrors.confirmPassword)}
                  aria-describedby={
                    formErrors.confirmPassword ? "confirm-password-error" : undefined
                  }
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 px-1 h-2/3 top-1/2 border-l-2 border-[#CFD9E0] -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {formErrors.confirmPassword && (
                <p
                  id="confirm-password-error"
                  className="mt-1 text-sm text-red-600"
                >
                  {formErrors.confirmPassword}
                </p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 border border-gray-300 rounded-md cursor-pointer accent-lime-400"
                />
                <span className="text-[#718096] font-normal">Remember me</span>
              </label>
              <button
                type="button"
                className="text-[#1C4532] font-medium hover:underline"
              >
                Forgot Password?
              </button>
            </div>

            {/* Sign Up Button */}
            <button
              type="submit"
              className="btn-cta w-full py-3 font-medium rounded-md mt-2 disabled:opacity-70"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? "Creating account..." : "Sign Up"}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 h-px bg-[#A0AEC0]" />
            <span className="text-sm text-[#718096]">OR</span>
            <div className="flex-1 h-px bg-[#A0AEC0]" />
          </div>

          {/* Social Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleGoogleLogin}
              className="relative w-full flex items-center justify-center px-4 py-3 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
              {/* SVG Icon on the left */}
              <svg className="absolute left-4 w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285f4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34a853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#fbbc05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#ea4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>

              {/* Centered Text */}
              <span className="text-[#67728A] font-medium">
                Continue with Google
              </span>
            </button>

            <button
              onClick={handleFacebookLogin}
              className="relative w-full flex items-center justify-center px-4 py-3 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
              <svg
                className="absolute left-4 w-5 h-5"
                fill="#1877f2"
                viewBox="0 0 24 24"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              <span className="text-[#67728A] font-medium">
                Continue with Facebook
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Right Section - Branding */}
      <div
        className="hidden lg:flex w-1/2 min-h-screen bg-cover bg-center items-center justify-center p-8"
        style={{ backgroundImage: "url('assets/images/bg-Img.png')" }}
      >
        <div className=" rounded-md flex items-center justify-center">
          <img
            src="/assets/images/DarkMode.png"
            alt="Join Us"
            className="w-48 h-auto object-contain"
          />
        </div>
      </div>
    </div>
  );
}
